import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { useTTS } from "../lib/useTTS";
import { orbLabel } from "../components/Orb";
import { SpaceshipHero } from "../components/SpaceshipHero";
import { Sidebar } from "../components/Sidebar";
import { VoiceBar } from "../components/VoiceBar";
import { ConversationStream } from "../components/ConversationStream";
import { ChatCompanion } from "../components/robot/ChatCompanion";
import { SettingsDialog } from "../components/SettingsDialog";
import { Workspace } from "../components/Workspace";
import { WalletModal } from "../components/WalletModal";
import { StudioDrawer } from "../components/StudioDrawer";
import { ControlCenter } from "../components/ControlCenter";
import { NotificationCenter } from "../components/NotificationCenter";
import { ProjectsDrawer } from "../components/ProjectsDrawer";
import { LearnDrawer } from "../components/learn/LearnDrawer";
import { PresencePill, presenceOf, buildGreeting, claimSessionGreeting, GreetingBubble } from "../components/Presence";

const IMAGE_ASK = /\b(wallpapers?|lock ?screen)\b|\b(create|generate|make|draw|design|paint|render|imagine)\b.*\b(image|picture|photo|pic|portrait|logo|artwork|illustration|drawing)\b/i;
const WALLPAPER_HOWTO = /\b(how (do|can|to)|where|set (my|the|a|this)|change (my|the))\b/i;
import { getRecognition, speechSupported, parseWake } from "../lib/speech";
import { Settings, Menu, LogOut, LayoutGrid, Volume2, VolumeX, Zap, Sparkles, Shield, MessageSquare, Mic as MicIcon, Terminal, Globe, FolderOpen, Hammer, GraduationCap, Radio, X as XIcon, Code2 } from "lucide-react";
import { toast } from "sonner";

const ANNA_AVATAR = "https://images.unsplash.com/photo-1777927519810-5feb98777717?crop=entropy&cs=srgb&fm=jpg&q=85&w=120";
const SAM_AVATAR = "https://images.unsplash.com/photo-1656947296515-3faa76840890?crop=entropy&cs=srgb&fm=jpg&q=85&w=120";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const QUICK = ["Good morning", "What can you actually do right now?", "I've got a lot on today", "Just wanted to talk"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const tts = useTTS();
  const [assistant, setAssistant] = useState(user.assistant_preference || "anna");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [baseState, setBaseState] = useState("idle"); // idle | thinking | listening
  const [thinking, setThinking] = useState(false);
  const [listening, setListening] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspaceKey, setWorkspaceKey] = useState(0);
  const [mode, setMode] = useState("text"); // "text" | "voice"
  const [balance, setBalance] = useState(user.credit_balance ?? 0);
  const [walletOpen, setWalletOpen] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const [controlOpen, setControlOpen] = useState(false);
  const [controlTab, setControlTab] = useState(null);
  const [controlGoal, setControlGoal] = useState(null);
  const [pendingControl, setPendingControl] = useState(null);
  const [pricing, setPricing] = useState(null);
  // Phase 5 — voice control
  const [voiceState, setVoiceState] = useState("idle"); // idle|listening|transcribing|review|executing|result
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voicePlan, setVoicePlan] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [instantScene, setInstantScene] = useState(false);
  const [showGreeting, setShowGreeting] = useState(() => claimSessionGreeting());
  const [greetSeed] = useState(() => Math.floor(Math.random() * 2));
  const [freeImage, setFreeImage] = useState(null);
  const [liveVoice, setLiveVoice] = useState(false);
  const [muted, setMuted] = useState(false);
  const liveRef = useRef(false);
  const mutedRef = useRef(false);
  const liveRecRef = useRef(null);
  const liveRunningRef = useRef(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const hasRecorder = typeof window !== "undefined" && !!(navigator.mediaDevices && window.MediaRecorder);
  const vlog = (stage, extra = "") => console.info(`[voice] ${stage}${extra ? " " + extra : ""}`);

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const micSupported = speechSupported();
  const voiceMode = mode === "voice";
  const voiceIdFor = (a) => user.voice_settings?.[`${a}_voice`];
  const orbState = tts.isSpeaking ? "speaking" : (thinking ? "thinking" : (listening ? "listening" : baseState));

  const loadConversations = useCallback(async (q) => {
    const { data } = await api.get("/conversations", { params: q ? { q } : {} });
    setConversations(data.conversations);
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { api.get("/wallet").then(({ data }) => { setBalance(data.balance); setPricing(data.pricing); setFreeImage(data.free_image); }).catch(() => {}); }, []);
  useEffect(() => {
    const onRefresh = () => api.get("/wallet").then(({ data }) => setBalance(data.balance)).catch(() => {});
    window.addEventListener("stealth:balance-refresh", onRefresh);
    return () => window.removeEventListener("stealth:balance-refresh", onRefresh);
  }, []);
  const refreshFree = useCallback(() => { api.get("/free-image").then(({ data }) => setFreeImage(data)).catch(() => {}); }, []);
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    if (messages.length === 0 && !thinking) el.scrollTo({ top: 0 });
    else el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      [setSettingsOpen, setWorkspaceOpen, setWalletOpen, setStudioOpen, setControlOpen, setLearnOpen, setProjectsOpen].forEach((f) => f(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Images/videos load after render: keep the latest message in view once their size is known.
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    const onLoad = (e) => {
      if (!["IMG", "VIDEO"].includes(e.target.tagName) || messages.length === 0) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < e.target.clientHeight + 160) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    };
    el.addEventListener("load", onLoad, true);
    el.addEventListener("loadeddata", onLoad, true);
    return () => { el.removeEventListener("load", onLoad, true); el.removeEventListener("loadeddata", onLoad, true); };
  }, [messages.length]);

  // Hands-free live voice: resume listening after Anna/Sam finishes speaking.
  useEffect(() => {
    if (!liveVoice || muted) return;
    if (!tts.isSpeaking && !thinking && voiceState !== "listening") {
      const id = setTimeout(() => startLiveListen(), 350);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVoice, muted, tts.isSpeaking, thinking, voiceState]);

  const runAgentFromNotification = (goal) => {
    setControlGoal(goal);
    setControlTab("agent");
    setControlOpen(true);
  };

  const selectConversation = async (id) => {
    setInstantScene(true);
    setActiveId(id);
    setSidebarOpen(false);
    tts.stop(); setBaseState("idle");
    const { data } = await api.get(`/conversations/${id}/messages`);
    setMessages(data.messages);
    const conv = conversations.find((c) => c.id === id);
    if (conv) setAssistant(conv.assistant);
  };

  const newConversation = () => {
    setInstantScene(false);
    setActiveId(null);
    setMessages([]);
    setSidebarOpen(false);
    tts.stop(); setBaseState("idle");
  };

  const send = async (msg) => {
    const hasAtt = attachments.length > 0;
    if (!msg.trim() && !hasAtt) return;
    setText("");
    tts.stop();
    const attForBody = attachments.map((a) => a.fname);
    const attMedia = attachments.map((a) => ({ url: a.url, kind: a.kind }));
    setAttachments([]);
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", text: msg, media: attMedia, created_at: new Date().toISOString() };
    setShowGreeting(false); setInstantScene(false);
    setMessages((m) => [...m, tempUser]);
    setThinking(true);
    try {
      const { data } = await api.post("/chat", {
        conversation_id: activeId, assistant, text: msg, speak: false, mode,
        attachments: attForBody,
        client_now: new Date().toISOString(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (!activeId) setActiveId(data.conversation_id);
      loadConversations();
      setThinking(false);
      setMessages((m) => [...m, { ...data.message, fresh: true }]);
      if (data.balance != null) setBalance(data.balance);
      refreshFree();
      if (data.actions && data.actions.length) setWorkspaceKey((k) => k + 1);
      if (data.control && data.control.type !== "unsupported") setPendingControl(data.control);
      // Poll any in-chat video that is still generating.
      (data.message.media || []).forEach((md) => {
        if (md.kind === "video" && md.status === "generating" && md.generation_id) {
          pollGeneration(md.generation_id, data.message.id);
        }
      });
      // Voice Chat only: speak the reply. Text Chat stays silent.
      if (voiceMode && data.message.text) tts.speak(data.message.id, data.message.text, { assistant, voiceId: voiceIdFor(assistant) });
    } catch (e) {
      setThinking(false);
      const detail = e.response?.data?.detail;
      if (detail === "insufficient_credits") {
        toast.error("You're out of Stealth Credits. Top up to keep chatting.");
        setWalletOpen(true);
      } else if (detail === "generation_failed") {
        toast.error("That image didn't come through — you weren't charged. Try again?");
      } else {
        if (detail !== "account_blocked") toast.error(typeof detail === "string" ? detail : "Looks like I'm having trouble connecting right now. Give me a second and try again.");
      }
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
    }
  };

  const onAttach = async (files) => {
    setUploading(true);
    try {
      const fd = new FormData();
      files.slice(0, 8).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, ...(data.attachments || [])].slice(0, 8));
    } catch (e) {
      if (e.response?.data?.detail !== "account_blocked") toast.error(e.response?.data?.detail || "Couldn't upload that file.");
    } finally { setUploading(false); }
  };
  const removeAttachment = (fname) => setAttachments((a) => a.filter((x) => x.fname !== fname));

  const pollGeneration = async (gid, messageId) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/studio/generations/${gid}`);
        const g = data.generation;
        if (g.status === "completed" && g.url) {
          setMessages((m) => m.map((x) => x.id === messageId
            ? { ...x, media: (x.media || []).map((md) => md.generation_id === gid ? { ...md, status: "completed", url: g.url } : md) } : x));
          if (data.balance != null) setBalance(data.balance);
          api.get("/wallet").then(({ data: w }) => setBalance(w.balance)).catch(() => {});
          return;
        }
        if (g.status === "failed") {
          setMessages((m) => m.map((x) => x.id === messageId
            ? { ...x, media: (x.media || []).map((md) => md.generation_id === gid ? { ...md, status: "failed" } : md) } : x));
          toast.error("Video generation failed — you weren't charged.");
          return;
        }
      } catch { return; }
    }
  };

  // Manual replay for a specific message (independent of Voice Mode).
  const replay = (m) => {
    if (!voiceMode) { toast("Switch to Voice Chat to hear responses."); return; }
    const a = m.assistant || "anna";
    tts.speak(m.id, m.text, { assistant: a, voiceId: voiceIdFor(a) });
  };

  const onMic = () => {
    if (tts.isSpeaking) { tts.stop(); }
    if (voiceState === "listening") { stopVoiceCapture(); return; }
    // Prefer on-device Web Speech; fall back to recording + Whisper (works on iOS Safari).
    if (micSupported) startWebSpeech();
    else if (hasRecorder) startRecording();
    else toast.error("Voice input isn't supported on this browser.");
  };

  const stopVoiceCapture = () => {
    if (recRef.current) { try { recRef.current.stop(); } catch (e) { console.debug(e?.message); } }
    if (mediaRef.current && mediaRef.current.state === "recording") { try { mediaRef.current.stop(); } catch (e) { console.debug(e?.message); } }
    setListening(false);
  };

  const startWebSpeech = () => {
    const rec = getRecognition();
    if (!rec) { if (hasRecorder) startRecording(); return; }
    let finalText = "";
    rec.onstart = () => { setListening(true); setVoiceState("listening"); vlog("voice-started", "(web-speech)"); };
    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t; else interim += t;
      }
      setVoiceTranscript(finalText || interim);
    };
    rec.onerror = (ev) => {
      setListening(false); setVoiceState("idle"); recRef.current = null;
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") toast.error("I need microphone access to hear you. Enable it in your browser settings.");
      else if (ev.error === "no-speech") toast.error("I didn't catch that. Try again?");
    };
    rec.onend = () => {
      setListening(false); recRef.current = null; vlog("voice-stopped");
      const finalMsg = (finalText || "").trim();
      if (finalMsg) { setVoiceTranscript(finalMsg); setVoiceState("review"); }
      else setVoiceState("idle");
    };
    recRef.current = rec;
    try { rec.start(); } catch (e) { console.debug("recognition start:", e?.message); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false); vlog("voice-stopped");
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 1200) { setVoiceState("idle"); toast.error("That was too short — try again."); return; }
        setVoiceState("transcribing"); vlog("transcription-started");
        try {
          const fd = new FormData();
          fd.append("audio", blob, mime === "audio/webm" ? "clip.webm" : "clip.mp4");
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          vlog("transcription-completed");
          const tr = (data.transcript || "").trim();
          if (!tr) { setVoiceState("idle"); toast.error("I couldn't hear anything. Try again?"); return; }
          setVoiceTranscript(tr); setVoiceState("review");
        } catch { setVoiceState("idle"); toast.error("I couldn't transcribe that. Try again?"); }
      };
      mediaRef.current = mr;
      mr.start();
      setListening(true); setVoiceState("listening"); vlog("voice-started", "(record)");
    } catch {
      setVoiceState("idle");
      toast.error("I need microphone access to hear you. Enable it in your browser settings.");
    }
  };

  const cancelVoice = () => { stopVoiceCapture(); setVoiceState("idle"); setVoiceTranscript(""); setVoicePlan([]); vlog("voice-cancelled"); };

  const runVoiceCommand = async (transcript) => {
    setVoiceState("executing"); vlog("command-parsed", `"${transcript.slice(0, 60)}"`);
    const tempUser = { id: `u_${Date.now()}`, role: "user", text: transcript };
    setShowGreeting(false); setInstantScene(false);
    setMessages((m) => [...m, tempUser]);
    try {
      const { data } = await api.post("/voice/execute", { transcript, mode: "voice" });
      vlog("execution-plan-created", `steps=${data.plan?.length || 0}`);
      setVoicePlan(data.plan || []);
      const reply = data.reply || "Done.";
      const aMsg = { id: `a_${Date.now()}`, role: "assistant", text: reply, assistant };
      setMessages((m) => [...m, { ...aMsg, fresh: true }]);
      tts.speak(aMsg.id, reply, { assistant, voiceId: voiceIdFor(assistant) });
      // Execute each planned step through the existing pipeline.
      (data.plan || []).forEach((p, idx) => {
        vlog(`action-${idx + 1}-started`, p.type);
        if (p.execution_target === "web" && !p.type?.startsWith("file") && p.params?.url) {
          window.open(p.params.url, "_blank", "noopener");
          vlog(`action-${idx + 1}-completed`, "opened tab");
        } else if (p.command_id) {
          pollCommand(p.command_id, p.label);
        }
      });
      vlog("execution-completed");
      setVoiceState("result");
      setTimeout(() => { setVoiceState("idle"); setVoiceTranscript(""); setVoicePlan([]); }, 4000);
    } catch (e) {
      setVoiceState("idle");
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
      if (e.response?.data?.detail !== "account_blocked") toast.error((e.response?.data?.detail) || "Voice command failed.");
    }
  };

  const markControl = (id, status) => api.post(`/control/activity/${id}/status`, { status }).catch(() => {});

  const pollCommand = async (cid, label) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await api.get(`/agent/commands/${cid}`);
        if (["success", "failed", "blocked", "unsupported", "timeout", "permission_required"].includes(data.status)) {
          if (data.status === "success") toast.success(`Done — ${label}.`);
          else toast.error(`Couldn't complete: ${label} (${data.status}).`);
          return;
        }
      } catch { return; }
    }
  };

  const runControl = async (c) => {
    if (c.execution_target === "web") {
      if (c.type && c.type.startsWith("file")) { setControlOpen(true); }
      else if (c.params?.url) { window.open(c.params.url, "_blank", "noopener"); markControl(c.log_id, "executed"); toast.success("Opened in a new tab."); }
      setPendingControl(null);
      return;
    }
    // desktop
    if (c.status === "agent_offline" || c.status === "permission_required") { setControlOpen(true); setPendingControl(null); return; }
    if (c.status === "pending") {
      try {
        const { data } = await api.post(`/control/execute/${c.log_id}`);
        toast(`Sent to ${data.device?.name || "your PC"}…`);
        pollCommand(data.command_id, c.label);
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(d === "agent_offline" ? "Your Desktop Agent is offline." : d === "permission_required" ? "That permission is off on your device." : "Couldn't send that.");
      }
      setPendingControl(null);
    }
  };
  const cancelControl = (c) => { markControl(c.log_id, "cancelled"); setPendingControl(null); };

  // auto-poll desktop actions that were sent immediately
  useEffect(() => {
    if (pendingControl?.status === "sent" && pendingControl.command_id) {
      pollCommand(pendingControl.command_id, pendingControl.label);
      setPendingControl(null);
    }
  }, [pendingControl]);

  // ---------- Hands-free live voice (continuous conversation) ----------
  const startLiveListen = () => {
    if (!liveRef.current || mutedRef.current || liveRunningRef.current) return;
    if (tts.isSpeaking || thinking) return;
    const rec = getRecognition(false);
    if (!rec) return;
    let finalText = "";
    liveRunningRef.current = true;
    rec.onstart = () => { setListening(true); setVoiceState("listening"); };
    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t; else interim += t;
      }
      setVoiceTranscript(finalText || interim);
    };
    rec.onerror = (e) => { if (e.error === "not-allowed") { toast.error("I need microphone access for hands-free."); stopLiveVoice(); } };
    rec.onend = () => {
      liveRunningRef.current = false; setListening(false); liveRecRef.current = null;
      const said = (finalText || "").trim();
      if (!liveRef.current) return;
      if (said) handleLiveTurn(said);
      // if nothing captured, the resume effect will restart listening
    };
    liveRecRef.current = rec;
    try { rec.start(); } catch (e) { liveRunningRef.current = false; }
  };

  const handleLiveTurn = (transcript) => {
    const { text: stripped, wake } = parseWake(transcript);
    if (wake && wake !== assistant) switchAssistant(wake);
    const msg = stripped || transcript;
    if (!msg.trim()) { return; }
    setVoiceState("processing");
    setVoiceTranscript(transcript);
    send(msg); // posts to /chat; voiceMode true → reply is spoken by useTTS
  };

  const startLiveVoice = async () => {
    if (!micSupported) { toast.error("Hands-free needs a browser with speech recognition (Chrome/Edge)."); return; }
    try { await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { toast.error("I need microphone access to start hands-free."); return; }
    setMode("voice"); setMuted(false); mutedRef.current = false;
    setLiveVoice(true); liveRef.current = true;
    setVoiceState("listening");
    toast("Hands-free on — just talk. Say “Hey Sam” to switch.");
    setTimeout(() => startLiveListen(), 200);
  };

  const stopLiveVoice = () => {
    liveRef.current = false; setLiveVoice(false);
    if (liveRecRef.current) { try { liveRecRef.current.stop(); } catch {} liveRecRef.current = null; }
    liveRunningRef.current = false;
    tts.stop(); setListening(false); setVoiceState("idle"); setVoiceTranscript("");
  };

  const toggleMute = () => {
    const next = !muted; setMuted(next); mutedRef.current = next;
    if (next) {
      if (liveRecRef.current) { try { liveRecRef.current.stop(); } catch {} }
      tts.stop(); setListening(false); setVoiceState("muted");
    } else {
      setVoiceState("listening"); setTimeout(() => startLiveListen(), 150);
    }
  };

  const switchAssistant = (a) => {
    if (a === assistant) return;
    setAssistant(a);
    tts.stop(); setBaseState("idle");
    toast(`${a === "sam" ? "Sam" : "Anna"} is here.`, { description: "Switched — the conversation stays with you." });
  };

  const renameConversation = async (id, title) => {
    await api.put(`/conversations/${id}`, { title });
    loadConversations();
  };
  const deleteConversation = async (id) => {
    await api.delete(`/conversations/${id}`);
    if (id === activeId) newConversation();
    loadConversations();
  };
  const pinConversation = async (c) => {
    await api.put(`/conversations/${c.id}`, { pinned: !c.pinned });
    loadConversations();
  };

  const name = user.preferred_name || "there";
  const accent = assistant === "sam" ? "#D6D6D6" : "#E4E4E4";
  // Welcome (cinematic) only for a brand-new chat; restored conversations open straight into the black chat view.
  const empty = !activeId && messages.length === 0;

  return (
    <div className="stealth-bg h-screen h-[100dvh] overflow-hidden flex">
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        onSelect={selectConversation}
        onNew={newConversation}
        onRename={renameConversation}
        onDelete={deleteConversation}
        onPin={pinConversation}
        onSearch={loadConversations}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`flex-1 flex flex-col min-w-0 min-h-0 lg:border-l lg:border-white/5 space-page ${empty ? "" : "is-chatting"} ${instantScene ? "instant" : ""}`} data-testid="space-page">
        <div className="space-starfield" aria-hidden="true" data-testid="space-starfield" />
        {/* Header */}
        <header className="glass-panel lg:bg-transparent lg:backdrop-blur-none lg:border-0 lg:shadow-none flex flex-wrap sm:flex-nowrap items-center justify-between gap-y-2 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-300" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar"><Menu className="w-5 h-5" /></button>
            <div>
              <div className="label-mono">Stealth Assistance</div>
              <h1 data-testid="app-header-greeting" className="font-heading text-xl sm:text-2xl font-medium mt-0.5 whitespace-nowrap">{greeting()}, {name}.</h1>
            </div>
          </div>
          <div className="header-actions flex items-center gap-1.5 sm:gap-2 min-w-0 w-full sm:w-auto overflow-x-auto sm:overflow-visible no-scrollbar" data-testid="header-actions">
            <NotificationCenter onRunAgent={runAgentFromNotification} />
            <button data-testid="wallet-chip" onClick={() => setWalletOpen(true)} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm glass-card text-mono-200 hover:border-mono-400/50 transition-all" title="Stealth Credits">
              <Zap className="w-4 h-4" /> <span data-testid="credit-balance">{balance}</span>
            </button>
            <button data-testid="open-studio" onClick={() => setStudioOpen(true)} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Creative Studio"><Sparkles className="w-5 h-5" /></button>
            <button data-testid="open-control" onClick={() => setControlOpen(true)} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Command Center"><Terminal className="w-5 h-5" /></button>
            <button data-testid="open-learn" onClick={() => setLearnOpen(true)} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Stealth Learn"><GraduationCap className="w-5 h-5" /></button>
            <button data-testid="open-stealth-code" onClick={() => { window.history.pushState(null, "", "/?code=1"); window.dispatchEvent(new PopStateEvent("popstate")); }} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Stealth Code"><Code2 className="w-5 h-5" /></button>
            <button data-testid="open-projects" onClick={() => setProjectsOpen(true)} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Build · My projects"><Hammer className="w-5 h-5" /></button>
            <button data-testid="open-workspace" onClick={() => { setWorkspaceOpen(true); setWorkspaceKey((k) => k + 1); }} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Tasks, reminders & notes"><LayoutGrid className="w-5 h-5" /></button>
            {user.is_admin && <button data-testid="open-admin" onClick={() => { window.history.pushState(null, "", "/?admin=users"); window.dispatchEvent(new PopStateEvent("popstate")); }} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all" title="Admin"><Shield className="w-5 h-5" /></button>}
            <button data-testid="open-settings" onClick={() => setSettingsOpen(true)} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all"><Settings className="w-5 h-5" /></button>
            <button data-testid="logout-button" onClick={logout} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-300 hover:text-red-400 hover:bg-white/5 transition-all"><LogOut className="w-5 h-5" /></button>
          </div>
        </header>

        {user.blocked && (
          <div data-testid="account-blocked-banner" className="relative z-[2] mx-4 sm:mx-6 mt-1 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">
            Your account is blocked, so new generations and credit spending are paused. Please contact support.
          </div>
        )}
        {/* Body */}
        <div className="space-body flex-1 min-h-0 flex flex-col items-center px-4 sm:px-6 pb-4 overflow-clip">
          <div className={`hero-backdrop ${empty ? "" : "is-chatting"} ${instantScene ? "instant" : ""}`} aria-hidden={!empty} data-testid="hero-backdrop">
            <SpaceshipHero state={orbState} full />
          </div>
          <div className="relative z-[1] w-full max-w-2xl flex-1 flex flex-col min-h-0">
            {!empty && (
                <div className="absolute top-0 inset-x-0 z-10 flex flex-wrap items-center justify-center gap-2 pt-1 pb-2" data-testid="chat-status-row">
                  <span className="hidden sm:flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1">
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
                    <span data-testid="orb-state-label" className="label-mono !text-slate-300">{orbLabel(orbState)}</span>
                  </span>
                  <PresencePill assistant={assistant} state={presenceOf({ muted: liveVoice && muted, speaking: tts.isSpeaking, thinking, listening })} />
                  {[{ key: "anna", label: "Anna" }, { key: "sam", label: "Sam" }].map((a) => (
                    <button key={a.key} data-testid={`assistant-selector-${a.key}`} onClick={() => switchAssistant(a.key)}
                      className={`text-xs rounded-full px-3.5 py-1.5 border transition-all ${assistant === a.key ? "border-mono-400/60 bg-mono-400/10 text-mono-200" : "border-white/10 text-slate-400 bg-black/40"}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            <div ref={streamRef} className={`flex-1 min-h-0 overflow-y-auto no-scrollbar pr-1 pb-4 ${empty ? "" : "stream-fade"}`} data-testid="chat-scroll-region">
              {empty ? <div className="hero-spacer" /> : <div className="h-14 shrink-0" />}
              {empty && (
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
                  <span data-testid="orb-state-label" className="label-mono !text-slate-300">{orbLabel(orbState)}</span>
                </div>
                <div className="mt-3">
                  <PresencePill assistant={assistant} state={presenceOf({ muted: liveVoice && muted, speaking: tts.isSpeaking, thinking, listening })} />
                </div>
              {empty && (
                <>
                  {showGreeting && <GreetingBubble assistant={assistant} text={buildGreeting(assistant, user.preferred_name, greetSeed)} />}
                  <p className="text-slate-400 mt-6 text-sm">Talk to Anna or Sam</p>
                  <div className="flex gap-3 mt-4">
                    {[
                      { key: "anna", label: "Anna", av: ANNA_AVATAR },
                      { key: "sam", label: "Sam", av: SAM_AVATAR },
                    ].map((a) => (
                      <button key={a.key} data-testid={`assistant-selector-${a.key}`} onClick={() => switchAssistant(a.key)}
                        className={`flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 border transition-all ${assistant === a.key ? "border-mono-400/60 bg-mono-400/5" : "border-white/10 hover:border-white/25"}`}>
                        <img src={a.av} alt={a.label} className="w-7 h-7 rounded-full object-cover" />
                        <span className="text-sm">{a.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center mt-8 max-w-md">
                    {QUICK.map((q) => (
                      <button key={q} data-testid="quick-action" onClick={() => send(q)}
                        className="text-xs text-slate-300 rounded-full px-3.5 py-2 glass-card hover:border-mono-400/40 transition-all">
                        {q}
                      </button>
                    ))}
                  </div>
                </>
              )}
              </div>
              )}
              {!empty && (
                <ConversationStream messages={messages} onReplay={replay} speakingId={tts.activeId} statusById={tts.statusById} thinking={thinking} assistant={assistant} liveState={listening ? "listening" : null} />
              )}
            </div>

            {!empty && <ChatCompanion messages={messages} thinking={thinking} listening={listening} speaking={tts.isSpeaking} typing={!!text.trim()} />}
            {/* Mode toggle + Voice bar */}
            <div className="w-full pt-2">
              {pendingControl && (
                <div data-testid="chat-control-card" className="mb-2 rounded-xl border border-mono-400/30 bg-mono-400/5 p-3 flex items-center gap-3">
                  {pendingControl.execution_target === "web" ? <Globe className="w-5 h-5 text-mono-300 shrink-0" /> : <Terminal className="w-5 h-5 text-mono-300 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="label-mono !text-mono-300">
                      {pendingControl.status === "agent_offline" ? "Desktop Agent offline"
                        : pendingControl.status === "permission_required" ? "Permission is off"
                        : "Stealth is ready to"}
                    </div>
                    <div className="text-sm truncate">{pendingControl.label}{pendingControl.device ? ` · ${pendingControl.device.name}` : ""}</div>
                  </div>
                  <button data-testid="chat-control-confirm" onClick={() => runControl(pendingControl)} className="rounded-full px-4 py-1.5 text-sm text-[#000000] font-medium shrink-0" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                    {pendingControl.status === "agent_offline" || pendingControl.status === "permission_required" ? "Devices"
                      : pendingControl.execution_target === "web" && !pendingControl.type?.startsWith("file") ? "Open" : pendingControl.execution_target === "desktop" ? "Confirm" : "Run"}
                  </button>
                  <button data-testid="chat-control-cancel" onClick={() => cancelControl(pendingControl)} className="rounded-full w-8 h-8 flex items-center justify-center text-slate-400 border border-white/10 shrink-0"><XIcon className="w-4 h-4" /></button>
                </div>
              )}
              <div className="flex items-center justify-center gap-1 mb-2 mx-auto w-fit rounded-full p-1 glass-card" data-testid="mode-toggle">
                <button data-testid="mode-text" onClick={() => { setMode("text"); tts.stop(); }}
                  className={`flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full transition-all ${!voiceMode ? "text-[#000000] font-medium" : "text-slate-400"}`}
                  style={!voiceMode ? { background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" } : {}}>
                  <MessageSquare className="w-3.5 h-3.5" /> Text Chat
                </button>
                <button data-testid="mode-voice" onClick={() => setMode("voice")}
                  className={`flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full transition-all ${voiceMode ? "text-[#000000] font-medium" : "text-slate-400"}`}
                  style={voiceMode ? { background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" } : {}}>
                  <MicIcon className="w-3.5 h-3.5" /> Voice Chat
                </button>
              </div>
              {voiceMode && tts.needsUnlock && (
                <button data-testid="enable-voice-banner" onClick={tts.enableVoice}
                  className="w-full mb-2 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 text-sm text-[#000000] font-medium"
                  style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}>
                  <Volume2 className="w-4 h-4" /> Tap to enable voice
                </button>
              )}
              {voiceState !== "idle" && (
                <div data-testid="voice-overlay" className="mb-2 rounded-2xl glass-panel border border-mono-400/30 p-4 fade-in-up">
                  <div className="flex items-center gap-2 mb-2">
                    {(() => {
                      const disp = muted ? "muted" : (tts.isSpeaking ? "speaking" : (thinking ? "processing" : voiceState));
                      const dot = disp === "listening" ? "bg-red-400 animate-pulse" : disp === "muted" ? "bg-slate-500" : disp === "speaking" ? "bg-mono-400 animate-pulse" : disp === "result" ? "bg-mono-400" : "bg-amber-400 animate-pulse";
                      const label = disp === "listening" ? "Listening…" : disp === "muted" ? "Muted" : disp === "speaking" ? "Speaking…" : disp === "processing" || disp === "transcribing" ? "Processing…" : disp === "review" ? "Ready to run" : disp === "executing" ? "Executing…" : "Done";
                      return (<>
                        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
                        <span data-testid="voice-state" className="text-xs font-medium tracking-wide text-slate-200 uppercase">{label}</span>
                      </>);
                    })()}
                    {liveVoice ? (
                      <div className="ml-auto flex items-center gap-3">
                        <button data-testid="voice-mute" onClick={toggleMute} className="text-xs text-mono-300 hover:underline flex items-center gap-1">
                          {muted ? <><Volume2 className="w-3.5 h-3.5" /> Unmute</> : <><VolumeX className="w-3.5 h-3.5" /> Mute</>}
                        </button>
                        <button data-testid="voice-end" onClick={stopLiveVoice} className="text-xs text-red-300 hover:underline">End</button>
                      </div>
                    ) : voiceState === "listening" ? (
                      <button data-testid="voice-stop" onClick={stopVoiceCapture} className="ml-auto text-xs text-mono-300 hover:underline">Stop</button>
                    ) : null}
                  </div>
                  {voiceTranscript && <div data-testid="voice-transcript" className="text-sm text-slate-100">“{voiceTranscript}”</div>}
                  {voicePlan.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="voice-plan">
                      {voicePlan.map((p) => (
                        <div key={p.index} className="flex items-center gap-2 text-xs text-slate-400">
                          <span className="text-mono-300">{p.index}.</span> {p.label}
                          <span className={`ml-auto ${p.status === "sent" || p.status === "ready" ? "text-mono-300" : p.status === "agent_offline" || p.status === "permission_required" ? "text-amber-300" : "text-slate-500"}`}>{p.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {voiceState === "review" && !liveVoice && (
                    <div className="flex gap-2 mt-3">
                      <button data-testid="voice-run" onClick={() => runVoiceCommand(voiceTranscript)}
                        className="rounded-full px-4 py-1.5 text-sm text-[#000000] font-medium" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>Run</button>
                      <button data-testid="voice-cancel" onClick={cancelVoice}
                        className="rounded-full px-4 py-1.5 text-sm text-slate-300 border border-white/10">Cancel</button>
                    </div>
                  )}
                </div>
              )}
              {voiceMode && !liveVoice && (
                <button data-testid="hands-free-start" onClick={startLiveVoice}
                  className="w-full mb-2 rounded-xl px-4 py-2 flex items-center justify-center gap-2 text-sm text-mono-200 border border-mono-400/30 hover:bg-mono-400/5 transition-all">
                  <Radio className="w-4 h-4" /> Start hands-free conversation
                </button>
              )}
              <VoiceBar
                text={text} setText={setText} onSend={send} onMic={onMic}
                listening={listening} speaking={tts.isSpeaking}
                disabled={thinking} micSupported={voiceMode && (micSupported || hasRecorder)}
                attachments={attachments} onAttach={onAttach} onRemoveAttachment={removeAttachment} uploading={uploading}
              />
              <p className="text-[11px] text-slate-500 text-center mt-2">
                {voiceMode ? "Voice Chat — Anna & Sam speak their replies." : "Text Chat — attach photos, generate images & videos, or just talk."}
              </p>
              {IMAGE_ASK.test(text) && !(WALLPAPER_HOWTO.test(text) && !/\b(create|generate|make|draw|design)\b/i.test(text)) && attachments.length === 0 && (
                <p data-testid="media-cost-preview" className="text-[11px] text-center mt-1 text-mono-200">
                  {/wallpaper|lock ?screen/i.test(text) ? "Wallpaper" : "Image"} · {freeImage?.available ? "Free — uses your daily free image" : `${pricing?.image ?? 20} credits (balance ${balance})`}
                </p>
              )}
              {freeImage && (
                <p data-testid="free-image-hint" className="text-[11px] text-center mt-1">
                  {freeImage.available
                    ? <span className="text-mono-300">✦ Free image available today</span>
                    : <span className="text-slate-500">Next free image {freeImage.next_at ? `${new Date(freeImage.next_at).toDateString() === new Date().toDateString() ? "today" : "tomorrow"} at ${new Date(freeImage.next_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "in ~24h"}</span>}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} onConversationsCleared={() => { newConversation(); loadConversations(); }} />
      <Workspace open={workspaceOpen} onClose={() => setWorkspaceOpen(false)} refreshKey={workspaceKey} />
      <WalletModal open={walletOpen} onClose={() => setWalletOpen(false)} balance={balance} onBalance={setBalance} />
      <StudioDrawer open={studioOpen} onClose={() => setStudioOpen(false)} pricing={pricing} onBalance={setBalance} />
      <ControlCenter open={controlOpen} onClose={() => { setControlOpen(false); setControlTab(null); setControlGoal(null); }} initialTab={controlTab} initialGoal={controlGoal} />
      <ProjectsDrawer open={projectsOpen} onClose={() => setProjectsOpen(false)} />
      <LearnDrawer open={learnOpen} onClose={() => setLearnOpen(false)} balance={balance} onBalance={() => api.get("/wallet").then(({ data }) => setBalance(data.balance)).catch(() => {})} />
    </div>
  );
}
